import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Shield, Cookie, Scale, Save, RefreshCw, Globe, Phone, Mail, MapPin, Building2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { id: "terms", label: "Términos de Servicio", icon: FileText, path: "/terminos", color: "text-blue-400 bg-blue-500/10" },
  { id: "privacy", label: "Política de Privacidad", icon: Shield, path: "/privacidad", color: "text-green-400 bg-green-500/10" },
  { id: "legal_notice", label: "Aviso Legal", icon: Scale, path: "/aviso-legal", color: "text-purple-400 bg-purple-500/10" },
  { id: "cookies", label: "Política de Cookies", icon: Cookie, path: "/cookies", color: "text-orange-400 bg-orange-500/10" },
];

const DEFAULT_INFO = {
  app_name: "OkBot",
  company_name: "OkGo Tech S.A. de C.V.",
  rfc: "OGT000000XXX",
  address: "Ciudad de México, México",
  email: "hola@okgo.mx",
  phone: "+52 55 0000 0000",
  website: "https://okbot.okgo.mx",
  support_email: "soporte@okgo.mx",
  dpo_email: "privacidad@okgo.mx",
  inai_registered: true,
  governing_law: "México",
  jurisdiction: "Ciudad de México",
};

const LegalSection = () => {
  const [info, setInfo] = useState(DEFAULT_INFO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");

  useEffect(() => {
    axios.get(`${API}/admin/legal/info`)
      .then(r => { if (r.data && Object.keys(r.data).length > 0) setInfo(p => ({ ...p, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setInfo(p => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/legal/info`, info);
      toast.success("Información legal guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const TABS = [
    { id: "company", label: "Empresa" },
    { id: "pages", label: "Páginas Legales" },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw size={24} className="text-red-400 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Scale size={20} className="text-red-400" />Marco Legal</h2>
          <p className="text-gray-500 text-sm mt-1">Información corporativa y páginas legales del sistema</p>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}Guardar Cambios
        </button>
      </div>

      <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? "bg-red-500 text-white" : "text-gray-400 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "company" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-panel p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Building2 size={16} className="text-red-400" />Datos Corporativos</h3>

            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre de la App / Plataforma</label>
              <input value={info.app_name} onChange={e => set("app_name", e.target.value)} className="input-glass w-full" placeholder="OkBot" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Razón Social</label>
              <input value={info.company_name} onChange={e => set("company_name", e.target.value)} className="input-glass w-full" placeholder="OkGo Tech S.A. de C.V." />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">RFC</label>
              <input value={info.rfc} onChange={e => set("rfc", e.target.value)} className="input-glass w-full" placeholder="RFC..." />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><MapPin size={11} />Domicilio Fiscal</label>
              <textarea value={info.address} onChange={e => set("address", e.target.value)} className="input-glass w-full resize-none" rows={2} placeholder="Calle, Colonia, Ciudad, CP, Estado, México" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Ley Aplicable</label>
                <input value={info.governing_law} onChange={e => set("governing_law", e.target.value)} className="input-glass w-full" placeholder="México" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Jurisdicción</label>
                <input value={info.jurisdiction} onChange={e => set("jurisdiction", e.target.value)} className="input-glass w-full" placeholder="Ciudad de México" />
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
              <input type="checkbox" id="inai" checked={info.inai_registered} onChange={e => set("inai_registered", e.target.checked)} className="w-4 h-4 accent-red-500" />
              <label htmlFor="inai" className="text-gray-300 text-sm cursor-pointer">Registrado ante el INAI (Padrón de Responsables de Datos Personales)</label>
            </div>
          </div>

          <div className="glass-panel p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Mail size={16} className="text-red-400" />Datos de Contacto</h3>

            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><Globe size={11} />Sitio Web</label>
              <input value={info.website} onChange={e => set("website", e.target.value)} className="input-glass w-full" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><Mail size={11} />Email Principal</label>
              <input value={info.email} onChange={e => set("email", e.target.value)} className="input-glass w-full" placeholder="hola@empresa.mx" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><Mail size={11} />Email de Soporte</label>
              <input value={info.support_email} onChange={e => set("support_email", e.target.value)} className="input-glass w-full" placeholder="soporte@empresa.mx" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><Shield size={11} />Email DPO / Privacidad (ARCO)</label>
              <input value={info.dpo_email} onChange={e => set("dpo_email", e.target.value)} className="input-glass w-full" placeholder="privacidad@empresa.mx" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1"><Phone size={11} />Teléfono</label>
              <input value={info.phone} onChange={e => set("phone", e.target.value)} className="input-glass w-full" placeholder="+52 55 0000 0000" />
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <p className="text-blue-300 text-xs">💡 Esta información se muestra automáticamente en las cuatro páginas legales del sistema (/terminos, /privacidad, /aviso-legal, /cookies).</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "pages" && (
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">Las páginas legales se generan automáticamente con la información de empresa ingresada arriba. Están disponibles en las siguientes rutas:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PAGES.map(page => {
              const Icon = page.icon;
              return (
                <div key={page.id} className="glass-panel p-5 flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${page.color}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{page.label}</p>
                    <p className="text-gray-500 text-xs mt-0.5 font-mono">{page.path}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <a href={page.path} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-white/8 hover:bg-white/15 text-gray-300 hover:text-white text-xs rounded-lg transition-colors flex items-center gap-1.5">
                        <Globe size={11} />Ver página
                      </a>
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <Check size={11} />Activa
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="glass-panel p-5">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><FileText size={14} className="text-red-400" />Marco Legal Aplicable</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "LFPDPPP — Ley Federal de Protección de Datos Personales en Posesión de los Particulares",
                "Reglamento de la LFPDPPP",
                "LFCE — Ley Federal de Comercio Electrónico",
                "LFCU — Ley Federal de los Consumidores (PROFECO)",
                "Lineamientos INAI para Aviso de Privacidad",
                "LFTR — Ley Federal de Telecomunicaciones y Radiodifusión",
              ].map(law => (
                <div key={law} className="flex items-start gap-2 p-3 bg-white/3 rounded-xl">
                  <span className="text-red-400 mt-0.5 flex-shrink-0">•</span>
                  <p className="text-gray-400 text-xs">{law}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalSection;
